import {
    PermissionCheckerService,
    FeatureCheckerService,
    LocalizationService,
    MessageService,
    AbpMultiTenancyService,
    NotifyService,
    SettingService,
} from 'abp-ng2-module';
import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AppConsts } from '@shared/AppConsts';
import { AppUrlService } from '@shared/common/nav/app-url.service';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { AppUiCustomizationService } from '@shared/common/ui/app-ui-customization.service';
import { PrimengTableHelper } from 'shared/helpers/PrimengTableHelper';
import { Message, SelectItem } from 'primeng/api';
import { ConfigServiceProxy, SironaSettingDto, UiCustomizationSettingsDto } from '@shared/service-proxies/service-proxies';
import '@shared/service-proxies/tenant-login-info-dto-extensions';
import { NgxSpinnerService } from 'ngx-spinner';
import { NgxSpinnerTextService } from '@app/shared/ngx-spinner-text.service';
import { relative } from 'path';
import { DateTime } from 'luxon';
import { ArchiveService } from '@app/shared/services/archive.service';

interface AbpEventSubscription {
    eventName: string;
    callback: (...args: any[]) => void;
}

@Component({
    template: '',
})
export abstract class AppComponentBase implements OnInit, OnDestroy {
    localizationSourceName = AppConsts.localization.defaultLocalizationSourceName;
    codes = AppConsts.codes;

    localization: LocalizationService;
    permission: PermissionCheckerService;
    feature: FeatureCheckerService;
    notify: NotifyService;
    setting: SettingService;
    message: MessageService;
    multiTenancy: AbpMultiTenancyService;
    appSession: AppSessionService;
    primengTableHelper: PrimengTableHelper;
    ui: AppUiCustomizationService;
    appUrlService: AppUrlService;
    spinnerService: NgxSpinnerService;
    eventSubscriptions: AbpEventSubscription[] = [];
    router: Router;
    route: ActivatedRoute;
    private ngxSpinnerTextService: NgxSpinnerTextService;
    archiveService: ArchiveService;
    isMobile: boolean;
    hasWritePermission: boolean;

    constructor(injector: Injector) {
        this.localization = injector.get(LocalizationService);
        this.permission = injector.get(PermissionCheckerService);
        this.feature = injector.get(FeatureCheckerService);
        this.notify = injector.get(NotifyService);
        this.setting = injector.get(SettingService);
        this.message = injector.get(MessageService);
        this.multiTenancy = injector.get(AbpMultiTenancyService);
        this.appSession = injector.get(AppSessionService);
        this.ui = injector.get(AppUiCustomizationService);
        this.appUrlService = injector.get(AppUrlService);
        this.primengTableHelper = new PrimengTableHelper();
        this.spinnerService = injector.get(NgxSpinnerService);
        this.ngxSpinnerTextService = injector.get(NgxSpinnerTextService);
        this.archiveService = injector.get(ArchiveService);
        this.router = injector.get(Router);
        this.route = injector.get(ActivatedRoute);
        this.isMobile = this.isMobileDevice();
    }

    ngOnInit(): void {
        if (this.archiveService.isArchived() === true) {
            this.hasWritePermission = false;
        }
    }

    get currentTheme(): UiCustomizationSettingsDto {
        return this.appSession.theme;
    }
    ngOnDestroy(): void {
        this.unSubscribeAllEvents();
    }

    flattenDeep(array) {
        return array.reduce(
            (acc, val) => (Array.isArray(val) ? acc.concat(this.flattenDeep(val)) : acc.concat(val)),
            []
        );
    }

    l(key: string, ...args: any[]): string {
        args.unshift(key);
        args.unshift(this.localizationSourceName);
        return this.ls.apply(this, args);
    }

    ls(sourcename: string, key: string, ...args: any[]): string {
        let localizedText = this.localization.localize(key, sourcename);

        if (localizedText === undefined || localizedText === null) {
            localizedText = key;
        }

        if (!args || !args.length) {
            return localizedText;
        }

        args.unshift(localizedText);
        return abp.utils.formatString.apply(this, this.flattenDeep(args));
    }

    lx(key: string): boolean {
        const l = this.localization.localize(key, this.localizationSourceName);
        return l && l !== key;
    }

    le(nameOfEnumSelectItemList: string, key: number): string {
        const list = (this[nameOfEnumSelectItemList] as SelectItem[]);
        if (!list) {
            return key.toString();
        }

        const item = list.find(x => x.value === key);
        return item ? item.label : key.toString();
    }

    isGranted(permissionName: string): boolean {
        return this.permission.isGranted(permissionName);
    }

    isGrantedAny(...permissions: string[]): boolean {
        if (!permissions) {
            return false;
        }

        for (const permission of permissions) {
            if (this.isGranted(permission)) {
                return true;
            }
        }

        return false;
    }

    s(key: string): string {
        return abp.setting.get(key);
    }

    appRootUrl(): string {
        return this.appUrlService.appRootUrl;
    }


    get containerClass(): string {
        if (this.appSession.theme.baseSettings.layout.layoutType === 'fluid') {
            return 'container-fluid';
        }

        return 'container';
    }

    showMainSpinner(text?: string): void {
        this.ngxSpinnerTextService.setText(text);
        this.spinnerService.show();
    }

    hideMainSpinner(text?: string): void {
        this.spinnerService.hide();
    }

    protected subscribeToEvent(eventName: string, callback: (...args: any[]) => void): void {
        abp.event.on(eventName, callback);
        this.eventSubscriptions.push({
            eventName,
            callback,
        });
    }

    getFileValidationMessage(file: File, maxFileSizeInBytes: number, allowedExtentions: string[]): Message {
        const ext = file.name.substr(file.name.lastIndexOf('.') + 1);

        if (!allowedExtentions.includes(ext.toLowerCase())) {
            return {
                severity: 'error',
                summary: file.name,
                detail: this.l('UploadError', this.l('File_Invalid_Type_Error'))
            };
        }

        if (file.size >= maxFileSizeInBytes) {
            return {
                severity: 'error',
                summary: file.name,
                detail: this.l('UploadError', this.l('File_SizeLimit_Error'))
            };
        }

    }

    getFileValidationMessages(files: File[], maxFileSizeInBytes: number, allowedExtentions: string[]): Message[] {
        let messages: Message[] = [];
        for (const file of files) {
            const message = this.getFileValidationMessage(file, maxFileSizeInBytes, allowedExtentions);
            if (message != null) {
                messages.push(message);
            }
        }

        return messages;
    }

    reloadCurrentRoute() {
        const currentUrl = this.router.url;
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate([currentUrl]);
        });
    }

    reloadToNewRoute(url: string) {
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate([url]);
        });
    }

    private unSubscribeAllEvents() {
        this.eventSubscriptions.forEach((s) => abp.event.off(s.eventName, s.callback));
        this.eventSubscriptions = [];
    }

    isMobileDevice(): boolean {
        return KTUtil.isMobileDevice();
    }

    protected removeBreakString(string: string) {
        return string.replace("<br>", '');
    }

    protected isEmpty(val: string) {
        return (val === undefined || val == null || val.length <= 0) ? true : false;
    }

    protected printDateTime(x: Date | DateTime):string {
        if (x instanceof Date) {
            return this.printDateTime(DateTime.fromJSDate(x));
        }

        if (x instanceof DateTime) {
            return x.toFormat('D');
        }

        return '';
    }

    // This method is used to ensure that a code block that should never be called is indeed never called.
    // It can be used in e.g. switch statements to ensure all enum values are handled.
    // If a new enum value is added and not handled, a compile-time error will occur.
    static neverToBeCalled(value: never): never {
        throw new Error(`This code should never be called! Value: ${value}`);
    }

    get cantonName(): string {
        return abp.canton.name;
    }
}
